import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { Plus, PencilSimple, Trash, Pulse, Play, Globe, X } from "@phosphor-icons/react";

const TYPES = ["http", "https", "ping", "tcp", "dns"];
const DNS_TYPES = ["A", "AAAA", "MX", "CNAME", "TXT", "NS"];

const EMPTY = {
  name: "", type: "https", target: "", client_id: "",
  interval_seconds: 60, timeout_seconds: 10,
  expected_status_codes: "200",
  expected_keyword: "",
  tcp_port: "",
  dns_record_type: "A",
  dns_expected_value: "",
  notes: "",
  enabled: true,
  alert_channel_ids: [],
  check_regions: ["default"],
  quorum_down: 1,
};

export default function Monitors() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [channels, setChannels] = useState([]);
  const [availableRegions, setAvailableRegions] = useState(["default"]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState({});
  const [regionsOpenFor, setRegionsOpenFor] = useState(null);
  const [regionsData, setRegionsData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, c, ch, rg] = await Promise.all([
        api.get("/monitors"),
        api.get("/clients"),
        api.get("/alert-channels"),
        api.get("/system/probe-regions").catch(() => ({ data: { regions: ["default"] } })),
      ]);
      setItems(m.data); setClients(c.data); setChannels(ch.data);
      setAvailableRegions(rg.data?.regions || ["default"]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); setError(""); };
  const openEdit = (m) => {
    setForm({
      ...EMPTY, ...m,
      client_id: m.client_id || "",
      expected_status_codes: (m.expected_status_codes || [200]).join(","),
      expected_keyword: m.expected_keyword || "",
      tcp_port: m.tcp_port || "",
      dns_record_type: m.dns_record_type || "A",
      dns_expected_value: m.dns_expected_value || "",
      notes: m.notes || "",
      alert_channel_ids: m.alert_channel_ids || [],
      check_regions: m.check_regions?.length ? m.check_regions : ["default"],
      quorum_down: m.quorum_down || 1,
    });
    setEditing(m); setError("");
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        name: form.name,
        type: form.type,
        target: form.target,
        client_id: form.client_id || null,
        interval_seconds: Number(form.interval_seconds) || 60,
        timeout_seconds: Number(form.timeout_seconds) || 10,
        expected_status_codes: String(form.expected_status_codes).split(",").map(s => parseInt(s.trim(), 10)).filter(Boolean),
        expected_keyword: form.expected_keyword || null,
        tcp_port: form.tcp_port ? Number(form.tcp_port) : null,
        dns_record_type: form.dns_record_type || "A",
        dns_expected_value: form.dns_expected_value || null,
        notes: form.notes || null,
        enabled: !!form.enabled,
        alert_channel_ids: form.alert_channel_ids || [],
        check_regions: form.check_regions?.length ? form.check_regions : ["default"],
        quorum_down: Math.max(1, Math.min(
          Number(form.quorum_down) || 1,
          (form.check_regions || ["default"]).length,
        )),
      };
      if (editing === "new") await api.post("/monitors", payload);
      else await api.put(`/monitors/${editing.id}`, payload);
      setEditing(null);
      await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete monitor?")) return;
    await api.delete(`/monitors/${id}`); await load();
  };

  const runCheck = async (id) => {
    setBusy(b => ({ ...b, [id]: true }));
    try { await api.post(`/monitors/${id}/check`); await load(); }
    finally { setBusy(b => ({ ...b, [id]: false })); }
  };

  const openRegionsView = async (m) => {
    setRegionsOpenFor(m);
    setRegionsData(null);
    try {
      const { data } = await api.get(`/monitors/${m.id}/uptime-by-region?hours=24`);
      setRegionsData(data);
    } catch (e) {
      setRegionsData({ error: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  const clientName = (id) => clients.find(c => c.id === id)?.name || "—";

  return (
    <Layout>
      <PageHeader
        title="Uptime monitors"
        subtitle="HTTP, Ping, TCP, DNS — checked on schedule"
        actions={
          <button onClick={openNew} data-testid="monitors-new-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold transition-colors">
            <Plus size={14} weight="bold" /> New monitor
          </button>
        }
      />
      <div className="p-6 space-y-4">
        <ProbeHealthBanner />
        {loading ? (
          <div className="text-sm text-slate-500">loading…</div>
        ) : items.length === 0 ? (
          <div className="sw-card border-dashed p-16 text-center">
            <Pulse size={36} weight="duotone" className="text-brand-cyan mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold tracking-tight mb-1">No monitors yet</h3>
            <p className="text-sm text-slate-400 mb-4">Add your first monitor — a website, an ISP gateway, a port, a DNS record.</p>
            <button onClick={openNew} data-testid="monitors-empty-cta"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">
              <Plus size={14} weight="bold" /> Add monitor
            </button>
          </div>
        ) : (
          <div className="sw-card overflow-hidden">
            <table className="w-full text-sm" data-testid="monitors-table">
              <thead className="bg-background-deep/60">
                <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Name</th>
                  <th className="text-left py-3 px-4">Type</th>
                  <th className="text-left py-3 px-4">Target</th>
                  <th className="text-left py-3 px-4">Client</th>
                  <th className="text-left py-3 px-4">24h uptime</th>
                  <th className="text-left py-3 px-4">Last check</th>
                  <th className="text-right py-3 px-4 w-40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id} className="border-t border-border hover:bg-white/5">
                    <td className="py-3 px-4"><StatusBadge status={m.last_status} /></td>
                    <td className="py-3 px-4">
                      <div className="text-white" data-testid={`monitor-name-${m.id}`}>{m.name}</div>
                      {m.last_response_ms != null && (
                        <div className="text-[11px] text-slate-500">{m.last_response_ms}ms</div>
                      )}
                      {(m.check_regions || ["default"]).length > 1 && (
                        <div className="flex flex-wrap gap-1 mt-1" data-testid={`monitor-regions-badges-${m.id}`}>
                          {(m.check_regions || []).map((r) => {
                            const rs = (m.region_status || {})[r];
                            const status = rs?.status || "pending";
                            const dot = status === "up" ? "bg-emerald-400"
                              : status === "degraded" ? "bg-amber-400"
                              : status === "down" ? "bg-red-400" : "bg-slate-500";
                            return (
                              <span key={r}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-background-deep border border-border rounded"
                                title={`${r}: ${status}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                                <span className="text-slate-400">{r}</span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs uppercase text-slate-300">{m.type}</td>
                    <td className="py-3 px-4 text-xs text-slate-400 truncate max-w-xs">{m.target}</td>
                    <td className="py-3 px-4 text-slate-400">{clientName(m.client_id)}</td>
                    <td className="py-3 px-4 text-xs">
                      <span className={(m.uptime_24h ?? 0) >= 99 ? "text-emerald-400" : (m.uptime_24h ?? 0) >= 95 ? "text-amber-400" : "text-red-400"}>
                        {(m.uptime_24h ?? 0).toFixed(2)}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-400 whitespace-nowrap">
                      {m.last_checked_at ? new Date(m.last_checked_at).toLocaleTimeString() : "never"}
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      {(m.check_regions || ["default"]).length > 1 && (
                        <button onClick={() => openRegionsView(m)}
                          data-testid={`monitor-regions-${m.id}`}
                          title="Per-region uptime"
                          className="p-1.5 text-slate-300 hover:text-brand-cyan"><Globe size={14} weight="duotone" /></button>
                      )}
                      <button onClick={() => runCheck(m.id)} disabled={busy[m.id]} data-testid={`monitor-check-${m.id}`}
                        className="p-1.5 text-slate-300 hover:text-brand-cyan disabled:opacity-50 ml-1" title="Run check now">
                        <Play size={14} weight={busy[m.id] ? "duotone" : "fill"} />
                      </button>
                      <button onClick={() => openEdit(m)} data-testid={`monitor-edit-${m.id}`}
                        className="p-1.5 text-slate-300 hover:text-white ml-1"><PencilSimple size={14} /></button>
                      <button onClick={() => remove(m.id)} data-testid={`monitor-delete-${m.id}`}
                        className="p-1.5 text-slate-300 hover:text-red-400 ml-1"><Trash size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="sw-card w-full max-w-xl p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="monitor-form">
            <h3 className="font-display text-lg font-bold tracking-tight">{editing === "new" ? "New monitor" : "Edit monitor"}</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" required>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="monitor-name-input" />
              </Field>
              <Field label="Type">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="monitor-type-select">
                  {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Target" required wide>
                <input required value={form.target} placeholder={
                  form.type === "http" || form.type === "https" ? "https://example.com" :
                  form.type === "ping" ? "1.1.1.1 or example.com" :
                  form.type === "tcp" ? "example.com" :
                  "example.com"
                } onChange={(e) => setForm({ ...form, target: e.target.value })} data-testid="monitor-target-input" />
              </Field>
              <Field label="Client">
                <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="monitor-client-select">
                  <option value="">(unassigned)</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Interval (seconds)">
                <input type="number" min="10" value={form.interval_seconds} onChange={(e) => setForm({ ...form, interval_seconds: e.target.value })} data-testid="monitor-interval-input" />
              </Field>
              <Field label="Timeout (seconds)">
                <input type="number" min="1" max="60" value={form.timeout_seconds} onChange={(e) => setForm({ ...form, timeout_seconds: e.target.value })} />
              </Field>
              {(form.type === "http" || form.type === "https") && (
                <>
                  <Field label="Expected codes (comma-sep)">
                    <input value={form.expected_status_codes} placeholder="200,204" onChange={(e) => setForm({ ...form, expected_status_codes: e.target.value })} />
                  </Field>
                  <Field label="Expected keyword (in body)">
                    <input value={form.expected_keyword} placeholder="(optional)" onChange={(e) => setForm({ ...form, expected_keyword: e.target.value })} />
                  </Field>
                </>
              )}
              {form.type === "tcp" && (
                <Field label="TCP port" required>
                  <input type="number" required value={form.tcp_port} onChange={(e) => setForm({ ...form, tcp_port: e.target.value })} />
                </Field>
              )}
              {form.type === "dns" && (
                <>
                  <Field label="Record type">
                    <select value={form.dns_record_type} onChange={(e) => setForm({ ...form, dns_record_type: e.target.value })}>
                      {DNS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Expected value (contains)">
                    <input value={form.dns_expected_value} placeholder="(optional)" onChange={(e) => setForm({ ...form, dns_expected_value: e.target.value })} />
                  </Field>
                </>
              )}
            </div>

            {/* Alert channels multi-select */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">Alert channels</label>
              {channels.length === 0 ? (
                <div className="text-xs text-slate-500">No channels yet. Create one in Alert channels.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {channels.map(ch => {
                    const on = form.alert_channel_ids.includes(ch.id);
                    return (
                      <button key={ch.id} type="button" onClick={() => {
                        setForm(f => ({
                          ...f,
                          alert_channel_ids: on
                            ? f.alert_channel_ids.filter(x => x !== ch.id)
                            : [...f.alert_channel_ids, ch.id],
                        }));
                      }}
                        className={`text-xs px-2 py-1 rounded border transition-colors ${on
                          ? "border-brand-cyan text-brand-cyan bg-brand-cyan/10"
                          : "border-border text-slate-300 hover:bg-white/5"}`}>
                        {ch.type} · {ch.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Multi-region check picker (only shown when >1 region available) */}
            {(availableRegions.length > 1 || (form.check_regions || []).length > 1) && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">
                  Check regions <Globe size={11} weight="duotone" className="inline text-brand-cyan ml-1" />
                </label>
                <div className="flex flex-wrap gap-2 mb-2" data-testid="monitor-regions-picker">
                  {availableRegions.map(r => {
                    const on = form.check_regions.includes(r);
                    return (
                      <button key={r} type="button" data-testid={`monitor-region-toggle-${r}`}
                        onClick={() => {
                          setForm(f => {
                            const next = on
                              ? f.check_regions.filter(x => x !== r)
                              : [...f.check_regions, r];
                            return { ...f, check_regions: next.length ? next : ["default"] };
                          });
                        }}
                        className={`text-xs px-2 py-1 rounded border transition-colors ${on
                          ? "border-brand-cyan text-brand-cyan bg-brand-cyan/10"
                          : "border-border text-slate-300 hover:bg-white/5"}`}>
                        {r}
                      </button>
                    );
                  })}
                </div>
                {(form.check_regions || []).length > 1 && (
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    Mark monitor down when at least
                    <input type="number" min="1" max={(form.check_regions || []).length}
                      value={form.quorum_down}
                      onChange={(e) => setForm({ ...form, quorum_down: e.target.value })}
                      data-testid="monitor-quorum-input"
                      className="w-16 bg-background-deep border border-border rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-brand-cyan" />
                    of {(form.check_regions || []).length} regions fail
                  </div>
                )}
              </div>
            )}

            <label className="flex items-center gap-2 text-sm text-slate-200 pt-2">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="monitor-enabled-checkbox" />
              Enabled
            </label>
            {error && <div className="text-xs text-red-400">{error}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={() => setEditing(null)} data-testid="monitor-cancel-btn"
                className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md">Cancel</button>
              <button type="submit" data-testid="monitor-save-btn"
                className="px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">Save</button>
            </div>
          </form>
        </div>
      )}
      {regionsOpenFor && (
        <div className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-6"
          onClick={() => setRegionsOpenFor(null)} data-testid="monitor-regions-modal">
          <div onClick={(e) => e.stopPropagation()}
            className="sw-card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 text-white font-semibold">
                  <Globe size={16} weight="duotone" className="text-brand-cyan" />
                  {regionsOpenFor.name}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Per-region uptime over the last 24h</div>
              </div>
              <button onClick={() => setRegionsOpenFor(null)}
                className="p-1.5 text-slate-400 hover:text-white"><X size={18} /></button>
            </div>
            {!regionsData ? (
              <div className="text-sm text-slate-500 text-center py-8">Loading…</div>
            ) : regionsData.error ? (
              <div className="text-sm text-red-400 text-center py-8">{regionsData.error}</div>
            ) : regionsData.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-8">No check data yet — try running a check.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                    <th className="text-left py-2 px-2">Region</th>
                    <th className="text-left py-2 px-2">Current</th>
                    <th className="text-right py-2 px-2">Uptime %</th>
                    <th className="text-right py-2 px-2">Median ms</th>
                    <th className="text-right py-2 px-2">Checks</th>
                  </tr>
                </thead>
                <tbody>
                  {regionsData.map(r => (
                    <tr key={r.region} className="border-t border-border">
                      <td className="py-2 px-2 text-white font-medium">{r.region}</td>
                      <td className="py-2 px-2">
                        <StatusBadge status={r.current_status || "pending"} />
                      </td>
                      <td className="py-2 px-2 text-right">
                        <span className={r.uptime_pct >= 99 ? "text-emerald-400"
                          : r.uptime_pct >= 95 ? "text-amber-400" : "text-red-400"}>
                          {r.uptime_pct != null ? `${r.uptime_pct.toFixed(2)}%` : "—"}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right text-slate-300">{r.median_response_ms ?? "—"}</td>
                      <td className="py-2 px-2 text-right text-slate-400">
                        {r.checks} <span className="text-[10px] text-slate-500">({r.down}↓ {r.degraded}▲ {r.up}↑)</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

function Field({ label, children, wide, required }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {React.cloneElement(children, {
        className: "w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan transition-colors",
      })}
    </div>
  );
}

function ProbeHealthBanner() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/system/probe-health");
        if (!cancelled) setData(data);
      } catch (err) {
        // Nice-to-have banner; hidden if PROBE_ENDPOINTS isn't configured.
        // Log at debug level so it doesn't spam production consoles.
        if (process.env.NODE_ENV !== "production") {
          console.debug("probe-health fetch failed:", err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data || !data.configured) return null; // hidden unless user wired up PROBE_ENDPOINTS

  return (
    <div className="sw-card p-4" data-testid="probe-health-banner">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Globe size={16} weight="duotone" className="text-brand-cyan" />
          <h3 className="text-sm font-semibold text-white tracking-tight">Edge probe health</h3>
        </div>
        <span className="text-[11px] text-slate-500">
          Live check via <span className="text-slate-400 font-mono">/system/probe-health</span>
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {(data.regions || []).map((r) => (
          <div key={r.region} data-testid={`probe-health-region-${r.region}`}
            className={`px-3 py-2 rounded-md border text-[12px] ${
              r.ok
                ? "border-emerald-500/30 bg-emerald-500/5"
                : "border-red-500/30 bg-red-500/5"
            }`}>
            <div className="flex items-center justify-between">
              <span className="text-white font-medium">{r.region}</span>
              <span className={r.ok ? "text-emerald-400" : "text-red-400"}>
                {r.ok ? "OK" : "DOWN"}
              </span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {r.ok ? (
                <>
                  {r.latency_ms}ms
                  {r.colo && <> · {r.colo}</>}
                  {r.cf_region && <> · {r.cf_region}</>}
                </>
              ) : (
                <span className="text-red-300">{r.error || "unreachable"}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
